import { useEffect, type DependencyList, type RefObject } from "react";

interface UseStickyHorizontalScrollbarOptions {
  targetRef: RefObject<HTMLElement | null>;
  barRef: RefObject<HTMLElement | null>;
  trackRef: RefObject<HTMLElement | null>;
  deps: DependencyList;
}

/**
 * Pins a sticky horizontal scrollbar to the bottom of the viewport that mirrors
 * the scroll position of a horizontally-overflowing target element. The bar is
 * hidden when the target has no overflow.
 */
export const useStickyHorizontalScrollbar = ({
  targetRef,
  barRef,
  trackRef,
  deps,
}: UseStickyHorizontalScrollbarOptions) => {
  useEffect(() => {
    const target = targetRef.current;
    const bar = barRef.current;
    const track = trackRef.current;
    if (!target || !bar || !track) return;

    const maxScroll = (el: HTMLElement) =>
      Math.max(0, el.scrollWidth - el.clientWidth);

    const syncScroll = (from: HTMLElement, to: HTMLElement) => {
      const fromMax = maxScroll(from);
      const toMax = maxScroll(to);
      if (fromMax === 0 || toMax === 0) return;
      const want = (from.scrollLeft / fromMax) * toMax;
      if (Math.abs(to.scrollLeft - want) > 1) to.scrollLeft = want;
    };

    const update = () => {
      const rect = target.getBoundingClientRect();
      track.style.width = `${target.scrollWidth}px`;
      bar.style.left = `${rect.left}px`;
      bar.style.width = `${rect.width}px`;
      const hasOverflow = target.scrollWidth > target.clientWidth + 1;
      bar.classList.toggle("hidden", !hasOverflow);
      syncScroll(target, bar);
    };

    const onBarScroll = () => syncScroll(bar, target);
    const onTargetScroll = () => syncScroll(target, bar);

    bar.addEventListener("scroll", onBarScroll, { passive: true });
    target.addEventListener("scroll", onTargetScroll, { passive: true });
    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("scroll", update, { passive: true });

    const ro = new ResizeObserver(update);
    ro.observe(target);
    update();

    return () => {
      bar.removeEventListener("scroll", onBarScroll);
      target.removeEventListener("scroll", onTargetScroll);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update);
      ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
